import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { JSONContent } from '@tiptap/react';
import {
  AgencyType,
  AllAgenciesDto,
  EmployerAgenciesDto,
} from '@zorro/clients';
import { formatDateISO, parseDateISO } from '@zorro/shared/formatters';
import {
  showWarningNotification,
  useForm,
  useImpersonation,
  useMonolithMutation,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { getEnrollmentInstructionsContent } from '../EnrollmentInstructions';
import {
  ZorroSetupFormFields,
  getZorroSetupSchemaObject,
  parseMonthlySEPDeadlineValuesFromOptions,
} from './ZorroSetupUtils';

type UseZorroSetupForm = (props?: {
  employerId?: string;
  isProspectMode?: boolean;
  isFinalizationMode?: boolean;
}) => {
  zorroSetupForm: UseFormReturn<ZorroSetupFormFields>;
  onSubmit: () => Promise<boolean>;
  allAgencies: AllAgenciesDto;
  defaultContentValue: JSONContent | null;
};

const blankAllAgencies: AllAgenciesDto = {
  zorroPartners: [],
  producers: [],
  enrollmentTeams: [],
};

const blankEmployerAgencies: EmployerAgenciesDto = {
  zorroPartner: null,
  producer: null,
  enrollmentTeams: [],
};

export const useZorroSetupForm: UseZorroSetupForm = ({
  employerId,
  isProspectMode,
  isFinalizationMode,
} = {}) => {
  const { user } = useImpersonation();
  const { isAgent, isEmployerAdmin, isZorroOperations, isLoading } = useRoles();
  const queryClient = useQueryClient();

  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: employerId && [employerId],
  });

  const { data: enrollmentInstructions } = useMonolithQuery({
    method: 'enrollmentInstructionsControllerFind',
    params: employerId && [employerId],
  });

  const { data: allAgencies = blankAllAgencies } = useMonolithQuery(
    isAgent
      ? {
          method: 'agenciesControllerFindAssignableAgencies',
          params: user && !isLoading && [user.tenantId],
          enabled: !isLoading && !isEmployerAdmin,
        }
      : {
          method: 'agenciesControllerFindAll',
          params: !isLoading && [],
          enabled: !isLoading && !isEmployerAdmin,
        }
  );

  const { data: employerAgencies = blankEmployerAgencies } = useMonolithQuery({
    method: 'agenciesControllerFindMany',
    params: employer && !isLoading && [employer.id],
  });

  const { tryMutate: updateEmployer } = useMonolithMutation({
    method: 'employersControllerUpdate',
    shouldInvalidateQueries: false,
  });

  const { tryMutate: updateEnrollmentInstructions } = useMonolithMutation({
    method: 'enrollmentInstructionsControllerUpsert',
    shouldInvalidateQueries: false,
  });

  const allAgenciesWithEmployerAgencies = useMemo(() => {
    const _allAgencies: AllAgenciesDto = structuredClone(allAgencies);

    const { zorroPartners, enrollmentTeams, producers } = _allAgencies;

    const allEmployerAgencies = [
      ...employerAgencies.enrollmentTeams,
      employerAgencies.zorroPartner,
      employerAgencies.producer,
    ].flatMap((agency) => agency ?? []);

    for (const employerAgency of allEmployerAgencies) {
      switch (employerAgency.type) {
        case AgencyType.ZORRO_PARTNER: {
          if (
            !zorroPartners.some(
              (agencyPartner) => agencyPartner.id === employerAgency.id
            )
          ) {
            zorroPartners.push(employerAgency);
          }
          break;
        }
        case AgencyType.ENROLLMENT_TEAM: {
          if (
            !enrollmentTeams.some(
              (agencyEnrollmentTeam) =>
                agencyEnrollmentTeam.id === employerAgency.id
            )
          ) {
            enrollmentTeams.push(employerAgency);
          }
          break;
        }
        case AgencyType.PRODUCER: {
          if (
            !producers.some(
              (agencyProducer) => agencyProducer.id === employerAgency.id
            )
          ) {
            producers.push(employerAgency);
          }
          break;
        }
      }
    }

    return _allAgencies;
  }, [allAgencies, employerAgencies]);

  const zorroSetupForm = useForm<ZorroSetupFormFields>({
    mode: 'all',
    resolver: yupResolver(
      getZorroSetupSchemaObject(isFinalizationMode, isProspectMode)
    ),
    defaultValues: {
      producerId: employer?.producerId || null,
      enrollmentTeamIds: employer?.enrollmentTeamIds || [],
      zorroPartnerId: isAgent
        ? employer?.zorroPartnerId || user?.tenantId || ''
        : employer?.zorroPartnerId || '',
      producerAgentId: employer?.producerAgentId || null,
      csmUserId: employer?.csmUserId || null,
      ccEmailList: employer?.ccEmailList || [],
      crmRecordId: employer?.crmRecordId
        ? Number.parseInt(employer?.crmRecordId)
        : null,
      consultationLink: employer?.consultationLink || '',
      enrollmentTeamResponsibilities:
        employer?.enrollmentTeamResponsibilities || '',
      prospectCoverageStartDate: employer?.prospectCoverageStartDate
        ? parseDateISO(employer.prospectCoverageStartDate).toDate()
        : undefined,
      monthlySEPDeadline: employer?.monthlySEPDeadline?.toString() || '0',
      enrollmentInstructions: enrollmentInstructions?.content || '',
      estimatedEmployees: employer?.estimatedEmployees || null,
      estimatedParticipationRate: employer?.estimatedParticipationRate || null,
      isPayrollSyncOn: employer?.isPayrollSyncOn ?? false,
      isPartnersHubOn: employer?.isPartnersHubOn ?? true,
      isEmploymentSyncOn: employer?.isEmploymentSyncOn ?? false,
    },
  });

  const { getValues } = zorroSetupForm;

  const defaultContentValue = getEnrollmentInstructionsContent(
    getValues('enrollmentInstructions')
  );

  return {
    allAgencies: allAgenciesWithEmployerAgencies,
    zorroSetupForm,
    defaultContentValue,
    onSubmit: async () => {
      const data = zorroSetupForm.getValues();

      if (!employerId) {
        return true;
      }

      const mutationResult = await updateEmployer([
        employerId,
        {
          producerId: data.producerId,
          enrollmentTeamIds: data.enrollmentTeamIds,
          zorroPartnerId: data.zorroPartnerId,
          crmRecordId: data.crmRecordId?.toString() || null,
          consultationLink: data.consultationLink,
          enrollmentTeamResponsibilities: data.enrollmentTeamResponsibilities,
          ccEmailList: data.ccEmailList || [],
          producerAgentId: data.producerAgentId,
          csmUserId: data.csmUserId,
          prospectCoverageStartDate: data.prospectCoverageStartDate
            ? formatDateISO(data.prospectCoverageStartDate)
            : undefined,
          ...parseMonthlySEPDeadlineValuesFromOptions(data.monthlySEPDeadline),
          estimatedEmployees: data.estimatedEmployees,
          estimatedParticipationRate: data.estimatedParticipationRate,
          isPayrollSyncOn: data.isPayrollSyncOn,
          isPartnersHubOn: data.isPartnersHubOn,
          isEmploymentSyncOn: data.isEmploymentSyncOn,
        },
      ]);
      if (mutationResult.isOk()) {
        if (
          mutationResult.value?.warnings &&
          mutationResult.value.warnings.length > 0
        ) {
          for (const warning of mutationResult.value.warnings) {
            showWarningNotification({
              message: warning,
            });
          }
        }
      }

      if (!isZorroOperations) {
        return mutationResult.isOk();
      }

      const mutationEnrollmentInstructions = await updateEnrollmentInstructions(
        [
          employerId,
          { content: data.enrollmentInstructions || JSON.stringify({}) },
        ]
      );
      setTimeout(async () => {
        await queryClient.invalidateQueries();
      }, 0);

      return mutationResult.isOk() && mutationEnrollmentInstructions.isOk();
    },
  };
};
